import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ProtectedRoute } from "@/components/shell/ProtectedRoute";
import { AppShell } from "@/components/shell/AppShell";
import LoginPage from "@/pages/LoginPage";
import ForcePasswordChangePage from "@/pages/ForcePasswordChangePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import PlayersListPage from "@/pages/PlayersListPage";
import PlayerProfilePage from "@/pages/PlayerProfilePage";
import EvaluationPage from "@/pages/EvaluationPage";
import DraftPlanPage from "@/pages/DraftPlanPage";
import TeamFitsPage from "@/pages/TeamFitsPage";
import InterviewPrepPage from "@/pages/InterviewPrepPage";
import InterviewSessionPage from "@/pages/InterviewSessionPage";
import ReportsPage from "@/pages/ReportsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import MessagingPage from "@/pages/MessagingPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import ComparisonBoardPage from "@/pages/ComparisonBoardPage";
import AICockpitPage from "@/pages/AICockpitPage";
import PitchDeckPage from "@/pages/PitchDeckPage";
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import LeaveBehindPage from "@/pages/LeaveBehindPage";
import AgreementPage from "@/pages/AgreementPage";
import CalendarPage from "@/pages/CalendarPage";
import FamilyPortalPage from "@/pages/FamilyPortalPage";
import AgentsPage from "@/pages/AgentsPage";
import EducationPage from "@/pages/EducationPage";
import PrepWorkspacePage from "@/pages/PrepWorkspacePage";
import DecisionsPage from "@/pages/DecisionsPage";
import ContractsPage from "@/pages/ContractsPage";
import ContractDetailPage from "@/pages/ContractDetailPage";

function RootGate() {
  // Authenticated users hop straight to the workspace they expect.
  // Everyone else gets the public marketing landing (Hero + CTAs).
  const { token, user } = useAuth();
  if (token) {
    if (user?.role === "family") return <Navigate to="/app/family" replace />;
    return <Navigate to="/app/dashboard" replace />;
  }
  return <LandingPage />;
}

function AppIndex() {
  // Default landing inside the authed shell — family lands on the Family Hub,
  // every other role keeps the existing Dashboard.
  const { user } = useAuth();
  if (user?.role === "family") return <Navigate to="/app/family" replace />;
  return <Navigate to="/app/dashboard" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
          <Route path="/pitch" element={<PitchDeckPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/leave-behind" element={<LeaveBehindPage />} />
          <Route path="/agreement" element={<AgreementPage />} />
          <Route
            path="/contracts/:contractId"
            element={
              <ProtectedRoute roles={["admin", "family", "player", "trainer"]}>
                <ContractDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AppIndex />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="family" element={<ProtectedRoute roles={["family", "admin"]}><FamilyPortalPage /></ProtectedRoute>} />
            <Route path="players" element={<ProtectedRoute roles={["admin"]}><PlayersListPage /></ProtectedRoute>} />
            <Route path="players/:playerId/profile" element={<PlayerProfilePage />} />
            <Route path="players/:playerId/evaluation" element={<EvaluationPage />} />
            <Route path="players/:playerId/draft-plan" element={<DraftPlanPage />} />
            <Route path="players/:playerId/team-fits" element={<TeamFitsPage />} />
            <Route path="players/:playerId/interview-prep" element={<InterviewPrepPage />} />
            <Route path="players/:playerId/interview-prep/sessions/:sessionId" element={<InterviewSessionPage />} />
            <Route path="players/:playerId/reports" element={<ReportsPage />} />
            <Route path="players/:playerId/documents" element={<DocumentsPage />} />
            <Route path="players/:playerId/comparison-board" element={<ComparisonBoardPage />} />
            <Route path="players/:playerId/agents" element={<AgentsPage />} />

            <Route path="me/profile" element={<PlayerProfilePage />} />
            <Route path="me/evaluation" element={<EvaluationPage />} />
            <Route path="me/draft-plan" element={<DraftPlanPage />} />
            <Route path="me/team-fits" element={<TeamFitsPage />} />
            <Route path="me/interview-prep" element={<InterviewPrepPage />} />
            <Route path="me/interview-prep/sessions/:sessionId" element={<InterviewSessionPage />} />
            <Route path="me/reports" element={<ReportsPage />} />
            <Route path="me/documents" element={<DocumentsPage />} />
            <Route path="me/comparison-board" element={<ComparisonBoardPage />} />
            <Route path="me/agents" element={<AgentsPage />} />
            <Route path="me/prep" element={<PrepWorkspacePage />} />
            <Route path="me/decisions" element={<DecisionsPage />} />
            <Route path="me/contracts" element={<ProtectedRoute roles={["family", "player", "trainer", "admin"]}><ContractsPage /></ProtectedRoute>} />
            <Route path="contracts" element={<ProtectedRoute roles={["admin"]}><ContractsPage /></ProtectedRoute>} />
            <Route path="players/:playerId/prep" element={<PrepWorkspacePage />} />
            <Route path="players/:playerId/decisions" element={<DecisionsPage />} />

            <Route path="messaging" element={<MessagingPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="education" element={<EducationPage />} />
            <Route path="admin" element={<ProtectedRoute roles={["admin"]}><AdminPanelPage /></ProtectedRoute>} />
            <Route path="ai-cockpit" element={<ProtectedRoute roles={["admin"]}><AICockpitPage /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
